import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, Lock, User, Building2, Truck, Users } from 'lucide-react'
import { firebaseAuthService } from '../services/firebaseAuthService'
import { useUser } from '../contexts/UserContext'

const SimpleRegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'VOLUNTEER' as 'FOOD_BANK' | 'SUPPLIER' | 'VOLUNTEER'
  })

  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { user, isAuthenticated, isLoading, refreshUser } = useUser()

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return
    if (user.userType !== 'ADMIN' && !user.emailVerified) {
      navigate('/verify-email', { replace: true })
    } else {
      navigate('/dashboard', { replace: true })
    }
  }, [isLoading, isAuthenticated, user, navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.email || !formData.password) {
      setError('Please fill in all required fields')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)

    try {
      const result = await firebaseAuthService.register(formData.email, formData.password, {
        firstName: '', // Names will be collected during onboarding
        lastName: '', // Names will be collected during onboarding
        phone: '', // Phone collected during onboarding
        userType: formData.userType,
        isVerified: false,
        onboardingCompleted: false // All users must complete onboarding
      })

      if (result.success) {
        // Flag: user just registered (used to trigger onboarding after email verification)
        sessionStorage.setItem('justRegistered', 'true')
        
        console.log('✅ Firebase user created and backend user registered')
        console.log('🔄 Refreshing user data after registration...')
        
        // Load profile from backend (registrationInProgress prevents wrong-type sync)
        await refreshUser()
        await new Promise(resolve => setTimeout(resolve, 300))
        
        console.log('✅ Registration complete, redirecting to email verification...')
        
        // Redirect to email verification - onboarding will happen after email is verified
        navigate('/verify-email')
      } else {
        setError(result.error || 'Registration failed')
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const userTypeOptions = [
    {
      value: 'FOOD_BANK',
      label: 'Food Bank',
      description: 'Receive donations and coordinate with volunteers',
      icon: Building2,
      color: 'primary'
    },
    {
      value: 'SUPPLIER',
      label: 'Supplier',
      description: 'Donate surplus food and reduce waste',
      icon: Truck,
      color: 'secondary'
    },
    {
      value: 'VOLUNTEER',
      label: 'Volunteer',
      description: 'Help transport food in your community',
      icon: Users,
      color: 'accent'
    }
  ]

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-primary-50/30 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-xl border border-neutral-100 p-8 md:p-10">
          <div>
            <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/20">
              <User className="h-7 w-7 text-white" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-neutral-900 tracking-tight">
              Create your account
            </h2>
            <p className="mt-2 text-center text-sm text-neutral-600">
              Join HarvestHelp and make a difference in your community
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                I am a... <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {userTypeOptions.map((option) => {
                  const Icon = option.icon
                  return (
                    <label
                      key={option.value}
                      className={`relative flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.userType === option.value
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name="userType"
                        value={option.value}
                        checked={formData.userType === option.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <Icon className={`h-5 w-5 mr-3 ${
                        formData.userType === option.value ? 'text-primary-600' : 'text-gray-400'
                      }`} />
                      <div>
                        <div className="font-medium text-gray-900">{option.label}</div>
                        <div className="text-sm text-gray-500">{option.description}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Password Fields */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Sign in
              </Link>
            </p>
          </div>
        </form>
        </div>
      </div>
    </div>
  )
}

export default SimpleRegisterPage
